import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { DomainCompetency } from "Stores/courseStore";
import DebouncedInput from "Components/Common/DebouncedInput";
import { MemoizedDomainStandardCompetenciesItem } from "./DomainStandardCompetenciesItem";

interface DomainStandardCompetenciesModalProps {
    show: boolean;
    onClose: (changesApplied?: boolean) => void;
    competencies?: DomainCompetency[];
    isLoading: boolean;
    toggleEnabled: (ruuid: string) => void;
}

export const DomainStandardCompetenciesModal = ({
    show,
    onClose,
    competencies = [],
    isLoading,
    toggleEnabled,
}: DomainStandardCompetenciesModalProps) => {
    const [searchValue, setSearchValue] = useState("");
    const [openMap, setOpenMap] = useState<Record<string, boolean>>({});
    const [changedCompetencies, setChangedCompetencies] = useState<Set<string>>(new Set());
    const [localToggleStates, setLocalToggleStates] = useState<Record<string, boolean>>({});
    const [selectedId, setSelectedId] = useState<string>("");

    useEffect(() => {
        if (show && competencies.length) {
            const states: Record<string, boolean> = {};
            const initLocalStates = (items: DomainCompetency[]) => {
                items.forEach((item) => {
                    states[item.ruuid] = item.school_enabled ?? item.enabled;
                    if (item.children.length > 0) {
                        initLocalStates(item.children);
                    }
                });
            };

            initLocalStates(competencies);
            setLocalToggleStates(states);
            setChangedCompetencies(new Set());
            setSelectedId("");
        }
    }, [show, competencies]);

    const filteredData = useMemo(() => {
        if (!competencies.length) return [];
        if (!searchValue.trim()) return competencies;

        const search = searchValue.toLowerCase();

        const filterCompetencies = (data: DomainCompetency[]): DomainCompetency[] => {
            const filtered: DomainCompetency[] = [];

            for (const comp of data) {
                const compName = comp.standard_name.toLowerCase();

                if (compName.includes(search)) {
                    filtered.push(comp);
                } else {
                    const filteredChildren = filterCompetencies(comp.children);
                    if (filteredChildren.length > 0) {
                        filtered.push({ ...comp, children: filteredChildren });
                    }
                }
            }

            return filtered;
        };

        return filterCompetencies(competencies);
    }, [competencies, searchValue]);

    useEffect(() => {
        if (searchValue.trim()) {
            const newMap: Record<string, boolean> = {};

            const expandAll = (items: DomainCompetency[]) => {
                items.forEach((item) => {
                    newMap[item.ruuid] = true;
                    if (item.children.length > 0) {
                        expandAll(item.children);
                    }
                });
            };

            expandAll(filteredData);
            setOpenMap(newMap);
        }
    }, [searchValue, filteredData]);

    const toggleOpen = useCallback((ruuid: string) => {
        setOpenMap((prev) => ({
            ...prev,
            [ruuid]: !prev[ruuid],
        }));
    }, []);

    const handleLocalToggle = useCallback((ruuid: string) => {
        const updateChildrenStates = (items: DomainCompetency[], parentState: boolean) => {
            items.forEach((item) => {
                setLocalToggleStates((prev) => ({
                    ...prev,
                    [item.ruuid]: parentState,
                }));
                if (item.children.length > 0) {
                    updateChildrenStates(item.children, parentState);
                }
            });
        };

        const findAndUpdateChildren = (items: DomainCompetency[], targetRuuid: string) => {
            for (const item of items) {
                if (item.ruuid === targetRuuid) {
                    const newState = !localToggleStates[item.ruuid];
                    setLocalToggleStates((prev) => ({
                        ...prev,
                        [item.ruuid]: newState,
                    }));
                    if (item.children.length > 0) {
                        updateChildrenStates(item.children, newState);
                    }
                    setChangedCompetencies((prev) => {
                        const newSet = new Set(prev);
                        newSet.add(item.ruuid);
                        return newSet;
                    });
                    return true;
                }
                if (item.children.length > 0 && findAndUpdateChildren(item.children, targetRuuid)) {
                    return true;
                }
            }
            return false;
        };

        findAndUpdateChildren(competencies, ruuid);
    }, [competencies, localToggleStates]);

    const getCompetencyToggleState = useCallback(
        (competency: DomainCompetency) => {
            return localToggleStates[competency.ruuid] ?? competency.school_enabled ?? competency.enabled;
        },
        [localToggleStates]
    );

    const handleApply = useCallback(async () => {
        try {
            await Promise.all(
                Array.from(changedCompetencies).map((ruuid) => toggleEnabled(ruuid))
            );
            onClose(true);
        } catch (error) {
            console.error("Error applying changes:", error);
            onClose(true);
        }
    }, [changedCompetencies, toggleEnabled, onClose]);

    const handleCancel = useCallback(() => {
        onClose(false);
    }, [onClose]);

    const handleItemClick = useCallback((ruuid: string) => {
        setSelectedId(ruuid);
    }, []);

    return (
        <Modal
            className="styledCompetenciesModal domain-competencies-modal"
            size="lg"
            show={show}
            onHide={handleCancel}
            scrollable
        >
            <Modal.Header className="headerModal">
                <i className="icon icon-close" onClick={handleCancel} />
                <div className="headerContainer">
                    <div className="title-container">
                        <h1>Select a competency</h1>
                    </div>
                    <div className="search-bar">
                        <DebouncedInput
                            type="text"
                            value={searchValue}
                            onChange={(value: string) => setSearchValue(value)}
                            placeholder="Search by Course title, subject number, curriculum year or credit hours"
                            className="search-input"
                        />
                        <i
                            className="type icon icon-search search-icon-bar"
                            aria-hidden="true"
                            data-testid="icon-search"
                        />
                    </div>
                </div>
            </Modal.Header>

            <Modal.Body>
                {filteredData.length > 0 ? (
                    filteredData.map((competency) => (
                        <MemoizedDomainStandardCompetenciesItem
                            key={competency.ruuid}
                            competency={competency}
                            level={1}
                            selectedId={selectedId}
                            openMap={openMap}
                            toggleOpen={toggleOpen}
                            onItemClick={handleItemClick}
                            toggleEnabled={handleLocalToggle}
                            isChecked={getCompetencyToggleState}
                        />
                    ))
                ) : (
                    <p className="no-competencies">No competencies found.</p>
                )}
            </Modal.Body>

            <Modal.Footer className="footerModal">
                <Button
                    className="applyButton"
                    variant="primary"
                    disabled={changedCompetencies.size === 0 || isLoading}
                    onClick={handleApply}
                >
                    {isLoading && <Spinner size="sm" className="buttonSpinner" />}
                    {isLoading ? "Loading..." : "Apply"}
                </Button>
                <Button onClick={handleCancel} className="cancelButton">
                    Cancel
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
